import React, { useMemo } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { Virtuoso } from 'react-virtuoso';
import { getDateKey, timestampToDateLabel } from '@/components/team-inbox/team-inbox-helpers';
import ViewBackupMessageRow from './view-backup-message-row';
import { useBackupSource, type BackupSource } from './use-backup-source';

// Large base index so the list can grow upward (older pages prepend) without
// the visible viewport jumping — Virtuoso holds position when firstItemIndex
// shrinks by exactly the number of prepended rows.
const FIRST_ITEM_INDEX = 1_000_000;

type DisplayItem =
    | { kind: 'header'; key: string; label: string }
    | { kind: 'message'; key: string; itemId: string };

/**
 * All-chats, upward-infinite virtualized message list for ONE storage tier
 * (vault or DB). Newest at the bottom; scrolling to the top pages in older
 * messages (WhatsApp-style), but across every chat since it's a verification
 * view. Rows reuse the team-inbox bubble via `ViewBackupMessageRow`.
 */
const AllChatsBackupList = React.memo(({ source }: { source: BackupSource }) => {
    const { items, hasMore, loading, loaded, error, loadOlder, count } = useBackupSource(source);

    // items are ascending (oldest→newest); insert a date separator whenever the
    // calendar day changes.
    const displayItems = useMemo<DisplayItem[]>(() => {
        const out: DisplayItem[] = [];
        let prevDay: string | null = null;
        for (const it of items) {
            const day = getDateKey(it.t);
            if (day !== prevDay) {
                out.push({ kind: 'header', key: `h_${day}`, label: timestampToDateLabel(it.t, false) });
                prevDay = day;
            }
            out.push({ kind: 'message', key: it._id, itemId: it._id });
        }
        return out;
    }, [items]);

    const firstItemIndex = useMemo(
        () => Math.max(0, FIRST_ITEM_INDEX - displayItems.length),
        [displayItems.length],
    );

    if (loaded && !loading && count === 0) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 1, color: 'var(--secondary-text-color, #667781)' }}>
                <Typography sx={{ fontSize: 15, fontWeight: 600 }}>
                    {source === 'vault' ? 'No messages in the local vault' : 'No messages in the DB backup'}
                </Typography>
                <Typography sx={{ fontSize: 12.5, maxWidth: 420, textAlign: 'center' }}>
                    {source === 'vault'
                        ? 'The vault is read through the WhatsApp Web engine — make sure a WhatsApp Web tab is open and connected.'
                        : 'Nothing has been backed up to the server DB yet (DB backup is opt-in and admin-gated).'}
                </Typography>
                {error && <Typography sx={{ fontSize: 12, color: 'var(--error-color, #d32f2f)' }}>{error}</Typography>}
            </Box>
        );
    }

    return (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{
                display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 0.75,
                fontSize: 12, color: 'var(--secondary-text-color, #667781)',
                bgcolor: 'var(--background-default, #fff)',
                borderBottom: '1px solid var(--divider-color, rgba(0,0,0,0.06))',
            }}>
                <span><b>{count}</b> loaded</span>
                {loading && <CircularProgress size={12} />}
                {!hasMore && !loading && <span>· all loaded</span>}
                {error && <span style={{ color: 'var(--error-color, #d32f2f)' }}>· {error}</span>}
            </Box>
            <Box sx={{ flex: 1, minHeight: 0, position: 'relative' }}>
            <Virtuoso
                data={displayItems}
                firstItemIndex={firstItemIndex}
                startReached={() => { if (hasMore && !loading) loadOlder(); }}
                initialTopMostItemIndex={Math.max(0, displayItems.length - 1)}
                followOutput={false}
                alignToBottom
                computeItemKey={(_index, item) => item.key}
                itemContent={(_index, item) =>
                    item.kind === 'header' ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: '10px' }}>
                            <Box sx={{
                                fontSize: 12,
                                fontWeight: 500,
                                color: 'var(--secondary-text-color, #54656f)',
                                background: 'var(--date-chip-bg, rgba(255,255,255,0.9))',
                                boxShadow: '0 1px 0.5px rgba(0,0,0,0.13)',
                                borderRadius: '7px',
                                padding: '5px 12px',
                            }}>
                                {item.label}
                            </Box>
                        </Box>
                    ) : (
                        <ViewBackupMessageRow itemId={item.itemId} />
                    )
                }
                components={{
                    Header: () =>
                        hasMore ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                                <CircularProgress size={22} />
                            </Box>
                        ) : (
                            <Box sx={{ textAlign: 'center', py: 1.5, fontSize: 11.5, color: 'var(--secondary-text-color, #8696a0)' }}>
                                — start of {source === 'vault' ? 'vault' : 'DB backup'} —
                            </Box>
                        ),
                    Footer: () => <Box sx={{ height: 12 }} />,
                }}
            />
            </Box>
        </Box>
    );
});

export default AllChatsBackupList;
